import {
  DOWNLOAD_BACKOFF_BASE_MS,
  DOWNLOAD_BACKOFF_MAX_MS,
  DOWNLOAD_MAX_ATTEMPTS,
} from '../constants.js';
import type { Logger } from '../log.js';

/**
 * Asset download with range-request resume and exponential backoff with jitter
 * (technical-design §11.5). The player never depends on a download completing:
 * a failure leaves the last known-good loop playing (§11.7), so this reports
 * failure rather than throwing past the caller.
 */

export interface HttpResponseLike {
  readonly status: number;
  readonly headers: { get(name: string): string | null };
  arrayBuffer(): Promise<ArrayBuffer>;
}

export type FetchLike = (
  url: string,
  init?: { headers?: Record<string, string>; signal?: AbortSignal },
) => Promise<HttpResponseLike>;

export type DownloadResult =
  | { readonly ok: true; readonly bytes: Uint8Array }
  | { readonly ok: false; readonly reason: string; readonly status?: number };

export interface DownloadOptions {
  readonly resumeFrom?: Uint8Array | null;
  /** Persists bytes accumulated so far when an attempt ends part-way (§11.5 range resume). */
  readonly onPartial?: (bytes: Uint8Array) => Promise<void>;
}

export interface Downloader {
  download(url: string, options?: DownloadOptions): Promise<DownloadResult>;
}

export interface DownloaderOptions {
  readonly fetch: FetchLike;
  readonly sleep: (ms: number) => Promise<void>;
  readonly random?: () => number;
  readonly logger: Logger;
  readonly maxAttempts?: number;
  /** Called with the bytes accumulated so far when an attempt fails part-way. */
  readonly onPartial?: (bytes: Uint8Array) => Promise<void>;
}

export function createDownloader(options: DownloaderOptions): Downloader {
  const random = options.random ?? Math.random;
  const maxAttempts = options.maxAttempts ?? DOWNLOAD_MAX_ATTEMPTS;

  const persistPartial = options.onPartial;

  return {
    async download(url, downloadOptions = {}) {
      let prefix = downloadOptions.resumeFrom ?? null;
      const onPartial = downloadOptions.onPartial ?? persistPartial;

      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        if (attempt > 0) await options.sleep(backoffMs(attempt, random));

        const headers: Record<string, string> = {};
        if (prefix !== null && prefix.byteLength > 0) {
          headers['Range'] = `bytes=${String(prefix.byteLength)}-`;
        }

        let response: HttpResponseLike;
        try {
          response = await options.fetch(url, { headers });
        } catch (error) {
          options.logger.warn('asset.download.attempt_failed', {
            url,
            attempt,
            reason: describe(error),
          });
          continue;
        }

        if (response.status === 200 || response.status === 206) {
          let body: Uint8Array;
          try {
            body = new Uint8Array(await response.arrayBuffer());
          } catch (error) {
            options.logger.warn('asset.download.body_failed', {
              url,
              attempt,
              reason: describe(error),
            });
            continue;
          }

          // A 200 to a range request means the server ignored it: start over.
          const bytes =
            response.status === 206 && prefix !== null ? concat(prefix, body) : body;
          const declared = contentRangeTotal(response.headers.get('content-range'));
          if (declared !== null && bytes.byteLength < declared) {
            prefix = bytes;
            await onPartial?.(bytes);
            options.logger.warn('asset.download.short_body', {
              url,
              attempt,
              have: bytes.byteLength,
              declared,
            });
            continue;
          }
          return { ok: true, bytes };
        }

        // A 4xx on an asset is not retryable: the signed URL is stale or revoked and
        // a fresh manifest is the only thing that fixes it.
        if (response.status >= 400 && response.status < 500 && response.status !== 416) {
          options.logger.warn('asset.download.rejected', { url, status: response.status });
          return { ok: false, reason: 'asset_url_rejected', status: response.status };
        }

        options.logger.warn('asset.download.retryable_status', { url, status: response.status });
      }

      return { ok: false, reason: 'asset_download_exhausted' };
    },
  };
}

export function backoffMs(attempt: number, random: () => number): number {
  const exponential = Math.min(
    DOWNLOAD_BACKOFF_MAX_MS,
    DOWNLOAD_BACKOFF_BASE_MS * 2 ** (attempt - 1),
  );
  return Math.round(exponential * (0.5 + random() * 0.5));
}

function concat(head: Uint8Array, tail: Uint8Array): Uint8Array {
  const out = new Uint8Array(head.byteLength + tail.byteLength);
  out.set(head, 0);
  out.set(tail, head.byteLength);
  return out;
}

function contentRangeTotal(header: string | null): number | null {
  if (header === null) return null;
  const match = /\/(\d+)$/.exec(header.trim());
  if (match === null) return null;
  const total = Number(match[1]);
  return Number.isSafeInteger(total) ? total : null;
}

function describe(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
